import { FilmSessionFragment, FilmTileFragment } from "@generated-types"
import { formatDateTime, formatNiceDate } from "@utils/date-format"
import { tv, VariantProps } from "@utils/tw"
import { EventBadges } from "./EventBadges"
import { Chip } from "@components/ui/Chip"
import { useId } from "react"
import { CheckIcon } from "@radix-ui/react-icons"

type Props = {
  film?: FilmTileFragment
  session: FilmSessionFragment
  selectable?: boolean
  checked?: boolean
  onCheckedChange?: (checked: Boolean) => void
} & VariantProps<typeof createStyles>

export function SelectableSessionItem(props: Props) {
  const styles = createStyles(props)
  const id = useId()

  const dateString = formatDateTime(props.session.info?.dateTime!)

  return (
    <div
      className={styles.base()}
      onPointerDown={(e) => {
        if (props.selectable) {
          props.onCheckedChange?.(!props.checked)
          e.preventDefault()
        }
      }}
    >
      <div className={styles.info()}>
        <div id={id} className={styles.dateTime()}>
          {dateString}
        </div>
        <div className={styles.tags()}>
          <EventBadges film={props.film} session={props.session} shape="rounded" />
          <Chip variant="high" shape="rounded" className={styles.venueChip()}>
            {props.session.info?.venue?.titleSimple}
          </Chip>
        </div>
      </div>
      {props.selectable && (
        <div className={styles.checkboxContainer()}>
          <div className={styles.checkbox()}>
            <input
              type="checkbox"
              aria-label={dateString}
              className="invisible absolute"
              checked={!!props.checked}
              onChange={(e) => props.onCheckedChange?.(!!e.currentTarget.checked)}
            />
            <CheckIcon className="block size-[100%] opacity-0 scale-50 group-has-[:checked]:scale-125 group-has-[:checked]:opacity-100 transition-all" />
          </div>
        </div>
      )}
    </div>
  )
}

const createStyles = tv({
  base: "type-ui-s group bg-bg-low flex p-[0.7em] rounded-md min-w-[160px] cursor-pointer transition-colors duration-75",
  slots: {
    info: "flex flex-col gap-2 flex-1",
    dateTime: "text-fg",
    tags: "flex items-center gap-1",
    checkboxContainer: "flex pr-3 pl-4 items-center justify-center",
    checkbox: "size-[12px] text-bg-high border border-current rounded-full transition-colors duration-75",
    venueChip: "pointer-events-none",
  },
  variants: {
    variant: {
      outline: {
        base: [
          "border bg-transparent border-bg-mid hover:border-bg-high",
          "hover:bg-bg-low/10 hover:border-fg/15",
          "has-[input:checked]:!border-orange has-[input:checked]:bg-orange/10",
        ],
        venueChip: "bg-bg-high/20",
        checkbox: "group-has-[input:checked]:text-orange",
      },
      default: {
        base: [
          "border bg-bg-low border-bg-low hover:border-bg-mid",
          "has-[input:checked]:!border-bg-high has-[input:checked]:bg-bg-mid",
        ],
        venueChip: "bg-bg-high/25",
        checkbox: "group-has-[input:checked]:text-fg",
      },
    },
  },
  defaultVariants: {
    variant: "default",
  },
})
